import { create } from "zustand";
import {
  fetchTokens,
  TokenResponse,
  TokenScope,
} from "@/components/main/utils/tokens/TokensApi";
import { PersonalAccessToken } from "target_wasm";
import { fetchWithInit } from "./fetchWithInit";
import {
  sortTokens,
  DEFAULT_EXPIRATION_TIME,
} from "@/components/main/utils/tokens/utils";

interface TokensState {
  tokens: PersonalAccessToken[];
  isLoading: boolean;
  error: string | null;
  dialogOpen: boolean;
  newToken: { description: string; expiration_time: number; scope: TokenScope };
  generatedToken: string | null;
  isGeneratingToken: boolean;
  fetchTokensList: (options?: {
    showLoading?: boolean;
    refresh?: boolean;
  }) => Promise<PersonalAccessToken[]>;
  addToken: (token: PersonalAccessToken) => void;
  removeToken: (tokenId: string) => void;
  setError: (message: string | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  resetDialog: () => void;
  updateNewToken: (
    updates: Partial<{
      description: string;
      expiration_time: number;
      scope: TokenScope;
    }>,
  ) => void;
  setGeneratedToken: (token: string | null) => void;
  setIsGeneratingToken: (loading: boolean) => void;
}

export const useTokensStore = create<TokensState>((set, get) => ({
  tokens: [],
  isLoading: false,
  error: null,
  dialogOpen: false,
  newToken: {
    description: "",
    expiration_time: DEFAULT_EXPIRATION_TIME,
    scope: "read",
  },
  generatedToken: null,
  isGeneratingToken: false,
  fetchTokensList: async (options) => {
    return fetchWithInit(set, options, {
      initFlag: "tokensInitialized",
      dataKey: "tokens",
      cachedData: get().tokens,
      fetchFn: async () => {
        const tokenList = await fetchTokens();
        return sortTokens(
          tokenList.map(
            (token: TokenResponse) =>
              new PersonalAccessToken(
                token.token_id,
                token.status,
                BigInt(token.expires_at * 1000),
                token.scope,
                token.description,
                BigInt(token.created_at),
                token.org_id ?? undefined,
              ),
          ),
        );
      },
      errorMessage: "Failed to fetch tokens",
    });
  },
  addToken: (token) =>
    set((state) => {
      const updated = [
        token,
        ...state.tokens.filter((t) => t.get_token_id !== token.get_token_id),
      ];
      return { tokens: sortTokens(updated) };
    }),
  removeToken: (tokenId) =>
    set((state) => {
      const updated = state.tokens.filter((t) => t.get_token_id !== tokenId);
      return { tokens: sortTokens(updated) };
    }),
  setError: (message) => set({ error: message }),
  openDialog: () => set({ dialogOpen: true }),
  closeDialog: () => set({ dialogOpen: false }),
  resetDialog: () =>
    set({
      dialogOpen: false,
      newToken: {
        description: "",
        expiration_time: DEFAULT_EXPIRATION_TIME,
        scope: "read",
      },
      generatedToken: null,
      isGeneratingToken: false,
    }),
  updateNewToken: (updates) =>
    set((state) => ({
      newToken: { ...state.newToken, ...updates },
    })),
  setGeneratedToken: (token) => set({ generatedToken: token }),
  setIsGeneratingToken: (loading) => set({ isGeneratingToken: loading }),
}));
